"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.XHRAutoCache = void 0;
const cache_1 = require("../cache");
const store_1 = require("../store");
const xhr_1 = require("./xhr");
/**
 * XHRAutoCache is a wrapper around an XHR implementation that provides caching capabilities.
 * It supports caching GET requests and clearing the cache after POST, PUT, PATCH, or DELETE requests.
 * Additionally, it implements an n-1 caching strategy where cached data is returned immediately,
 * and the cache is updated in the background.
 */
class XHRAutoCache {
    /**
     * Constructor for XHRAutoCache.
     * @param options - Configuration options for the cache, XHR implementation, and cache TTL.
     */
    constructor(options) {
        this.xhr = (options === null || options === void 0 ? void 0 : options.xhr) || new xhr_1.XHR(options);
        this.cacheTTL = (options === null || options === void 0 ? void 0 : options.cacheTTL) || 1000;
        this.cache = (options === null || options === void 0 ? void 0 : options.cache) || new cache_1.CacheV2(new store_1.Store({ storage: store_1.StorageLocation.Memory }));
    }
    /**
     * Creates a unique cache key based on the URL and request body.
     * @param url - The request URL.
     * @param body - Optional request body parameters.
     * @returns A string representing the unique cache key.
     */
    createCacheKey(url, body) {
        let key = `${url}`;
        if (body) {
            const sortedBodyKeys = Object.keys(body).sort();
            const sortedBody = sortedBodyKeys.reduce((obj, key) => {
                obj[key] = body[key];
                return obj;
            }, {});
            key += `:${JSON.stringify(sortedBody)}`;
        }
        return key;
    }
    /**
     * Clears all cached data.
     * @deprecated This method is deprecated and will be removed in future versions.
     * Use the `cleanCache` method instead.
     */
    clearCache() {
        this.cache.cleanCache();
    }
    /**
     * Performs a GET request with caching support.
     * If cached data is available, it is returned immediately.
     * If `useBackgroundUpdate` is true, the cache is updated in the background.
     * @param urlRaw - The request URL.
     * @param xhrOptions - Optional XHR options.
     * @param useBackgroundUpdate - Whether to update the cache in the background.
     * @returns A promise resolving to the response.
     */
    GET(urlRaw, xhrOptions, useBackgroundUpdate) {
        return __awaiter(this, void 0, void 0, function* () {
            const key = this.createCacheKey(urlRaw, xhrOptions === null || xhrOptions === void 0 ? void 0 : xhrOptions.params);
            const cachedData = this.cache.getCached(key);
            if (cachedData) {
                // If n-1 caching is enabled, return cached data and update the cache in the background
                if (useBackgroundUpdate) {
                    this.updateCacheInBackground(urlRaw, xhrOptions, key);
                }
                return cachedData;
            }
            // If no cached data is available, perform the request as usual
            const response = yield this.xhr.GET(urlRaw, xhrOptions);
            if (response.ok) {
                this.cache.setCached(key, response, this.cacheTTL);
            }
            return response;
        });
    }
    /**
     * Updates the cache in the background by performing a GET request.
     * This method is called internally when `useBackgroundUpdate` is true.
     * @param urlRaw - The request URL.
     * @param xhrOptions - Optional XHR options.
     * @param key - The cache key to update.
     */
    updateCacheInBackground(urlRaw, xhrOptions, key) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield this.xhr.GET(urlRaw, xhrOptions);
                if (response.ok) {
                    this.cache.setCached(key, response, this.cacheTTL);
                }
            }
            catch (error) {
                console.error('Error updating cache in background:', error);
            }
        });
    }
    /**
     * Performs a POST request and clears the cache if the request is successful.
     * @param url - The request URL.
     * @param body - Optional request body.
     * @param xhrOptions - Optional XHR options.
     * @returns A promise resolving to the response.
     */
    POST(url, body, xhrOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.xhr.POST(url, body, xhrOptions);
            if (response.ok) {
                this.cache.cleanCache();
            }
            return response;
        });
    }
    /**
     * Performs a PUT request and clears the cache if the request is successful.
     * @param url - The request URL.
     * @param body - Optional request body.
     * @param xhrOptions - Optional XHR options.
     * @returns A promise resolving to the response.
     */
    PUT(url, body, xhrOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.xhr.PUT(url, body, xhrOptions);
            if (response.ok) {
                this.cache.cleanCache();
            }
            return response;
        });
    }
    /**
     * Performs a PATCH request and clears the cache if the request is successful.
     * @param url - The request URL.
     * @param body - Optional request body.
     * @param xhrOptions - Optional XHR options.
     * @returns A promise resolving to the response.
     */
    PATCH(url, body, xhrOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.xhr.PATCH(url, body, xhrOptions);
            if (response.ok) {
                this.cache.cleanCache();
            }
            return response;
        });
    }
    /**
     * Performs a DELETE request and clears the cache if the request is successful.
     * @param url - The request URL.
     * @param body - Optional request body.
     * @param xhrOptions - Optional XHR options.
     * @returns A promise resolving to the response.
     */
    DELETE(url, body, xhrOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.xhr.DELETE(url, body, xhrOptions);
            if (response.ok) {
                this.cache.cleanCache();
            }
            return response;
        });
    }
}
exports.XHRAutoCache = XHRAutoCache;
//# sourceMappingURL=xhrAutoCache.js.map